import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { getOpsTemplates, renderOpsTemplate } from '../../../requests';
import { apiMessage } from '../../../requests/opsInstructors';
import {
  cancelOpsMessage,
  getOpsAutomations,
  getOpsCommsStatus,
  getOpsOutbox,
  patchOpsTemplate,
  queueOpsMessage,
  runOpsAutomation,
  sendOpsMessage,
  TOpsAutomationRun,
  TOpsAutomationRunItem,
} from '../../../requests/opsCommsExtra';
import { TOpsCommsStatus, TOpsMessageCategory } from '../../../types';
import {
  btnDanger,
  btnGhost,
  btnPrimary,
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  labelClass,
  PageBody,
  PageHeader,
  Pagination,
  Pill,
  rowClickClass,
  tableClass,
  tdClass,
  theadClass,
  thClass,
  Toolbar,
} from '../ui';

/** Fields the render endpoint returns beyond the original shared type. */
type TRenderExtra = {
  edited?: boolean;
  suggestedTo?: string | null;
  rawSubject?: string;
  rawBody?: string;
};

const CATEGORIES: { value: TOpsMessageCategory; label: string }[] = [
  { value: 'manual', label: 'Manual' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'confirmation', label: 'Confirmation' },
  { value: 'cancellation', label: 'Cancellation notice' },
  { value: 'reschedule', label: 'Reschedule' },
  { value: 'program-welcome', label: 'Team Pass welcome' },
  { value: 'prep-call', label: 'Prep-call request' },
  { value: 'travel', label: 'Travel' },
  { value: 'kickoff', label: 'Kickoff' },
  { value: 'post-class', label: 'Post-class nudge' },
  { value: 'internal', label: 'Internal' },
];

const categoryLabel = (value: string | null) =>
  CATEGORIES.find((c) => c.value === value)?.label ?? value ?? '—';

const OUTBOX_STATUSES = ['held', 'sent', 'failed', 'suppressed', 'cancelled'];

/** "a@x.com, b@y.com" → ['a@x.com', 'b@y.com'] (commas, semicolons or whitespace). */
const splitAddresses = (text: string): string[] =>
  text
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);

/** Highlights unresolved merge-field markers (⟦Object.Field⟧) in plain text. */
const renderWithMarkers = (text: string): React.ReactNode[] =>
  text.split(/(⟦[^⟧]*⟧)/g).map((part, i) =>
    part.startsWith('⟦') ? (
      <span key={i} className="font-medium text-red-one">
        {part}
      </span>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );

const outboxPill = (status: string | null, holdReason: string | null) => {
  if (status === 'held') return <Pill value="held" tone="warn" />;
  if (status === 'sent') return <Pill value="sent" tone="good" />;
  if (status === 'failed') return <Pill value="failed" tone="bad" />;
  if (status === 'suppressed')
    return (
      <span title={holdReason ?? undefined}>
        <Pill value="suppressed" tone="neutral" />
      </span>
    );
  if (status === 'cancelled') return <Pill value="cancelled" tone="neutral" />;
  return <Pill value={status ?? '—'} tone="neutral" />;
};

/** Mode, from-address and the safety rails, in one calm line. */
const StatusStrip: React.FC<{ status: TOpsCommsStatus }> = ({ status }) => {
  const live = status.mode === 'live';
  return (
    <div className="px-8 pb-4">
      <div
        className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border px-4 py-2.5 text-sm ${
          live
            ? 'border-green-500 bg-green-500 text-green-900'
            : 'border-white-two bg-grey-50 text-gray-five'
        }`}
      >
        {live ? (
          <span>
            <b>Sending is on</b> · from {status.from}
          </span>
        ) : (
          <span>
            <b>Sending is off</b> — queued mail is held in the outbox
          </span>
        )}
        {live && status.redirectTo && (
          <span className="rounded-md bg-yellow-500 px-2 py-0.5 text-xs font-medium text-yellow-900">
            all mail redirected to {status.redirectTo}
          </span>
        )}
        {live && !status.configured && (
          <span className="rounded-md bg-red-500 px-2 py-0.5 text-xs font-medium text-red-900">
            mailbox credential not configured — missing{' '}
            {status.missing.join(', ')}
          </span>
        )}
        {status.allowlist.length > 0 && (
          <span className="text-xs text-gray-bold">
            allowlist: {status.allowlist.join(', ')}
          </span>
        )}
      </div>
    </div>
  );
};

const outcomeTone = (o: TOpsAutomationRunItem['outcome']) =>
  o === 'queued'
    ? 'good'
    : o === 'would-queue'
    ? 'info'
    : o === 'failed'
    ? 'bad'
    : o === 'suppressed'
    ? 'warn'
    : 'neutral';

const fmtRunAt = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const countsLine = (c: TOpsAutomationRun['counts']) =>
  [
    c.queued ? `${c.queued} queued` : null,
    c['would-queue'] ? `${c['would-queue']} would queue` : null,
    c['already-queued'] ? `${c['already-queued']} already queued` : null,
    c.suppressed ? `${c.suppressed} quiet account` : null,
    c.skipped ? `${c.skipped} skipped` : null,
    c.failed ? `${c.failed} failed` : null,
  ]
    .filter(Boolean)
    .join(' · ') || 'nothing due';

/**
 * The triggered sends: each rule, what it uses, when it last ran and what
 * is due now. Preview never queues; Run now queues (held, in dry-run) and
 * is disabled while EMAIL_AUTOMATIONS is off.
 */
const AutomationsCard: React.FC = () => {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<TOpsAutomationRun | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['opsAutomations'],
    queryFn: () => getOpsAutomations(),
    refetchInterval: 60_000,
  });

  const run = useMutation({
    mutationFn: ({ key, preview }: { key: string; preview: boolean }) =>
      runOpsAutomation(key, preview),
    onMutate: ({ key }) => setBusyKey(key),
    onSuccess: (r) => {
      setResult(r);
      if (!r.ran) {
        showToast(r.reason ?? 'Did not run', 'error');
        return;
      }
      showToast(
        `${r.key}: ${countsLine(r.counts)}${r.preview ? ' (preview)' : ''}`,
        r.counts.failed ? 'error' : 'success',
      );
      queryClient.invalidateQueries({ queryKey: ['opsAutomations'] });
      queryClient.invalidateQueries({ queryKey: ['opsOutbox'] });
    },
    onError: (e: unknown) => showToast(apiMessage(e), 'error'),
    onSettled: () => setBusyKey(null),
  });

  const mode = data?.mode ?? 'off';
  const modeTone =
    mode === 'live' ? 'good' : mode === 'dry-run' ? 'warn' : 'neutral';

  return (
    <Card
      title="Automations"
      right={
        data && (
          <span className="flex flex-wrap items-center gap-2 text-xs text-gray-bold">
            <Pill value={`automations ${mode}`} tone={modeTone} />
            <span>internal notes go to {data.schedulerTo}</span>
          </span>
        )
      }
    >
      <div className="px-5 py-3 text-xs text-gray-bold">
        {mode === 'off' && (
          <>
            Scheduled sends are <b>off</b> — Preview shows what each rule would
            queue; nothing is written. Set{' '}
            <code>EMAIL_AUTOMATIONS=dry-run</code> to queue held rows for
            review, <code>live</code> to send.
          </>
        )}
        {mode === 'dry-run' && (
          <>
            Rules run on their schedule and queue rows <b>held for review</b> in
            the outbox; nothing leaves until the mode is <code>live</code>.
          </>
        )}
        {mode === 'live' && (
          <>
            Rules run on their schedule and send through the normal path
            {data?.sendMode !== 'live' &&
              ' — but EMAIL_SEND_MODE is dry-run, so rows are still held'}
            .
            {data?.pausedRules.length ? (
              <>
                {' '}
                Paused (schedule held while the Salesforce twin is still on):{' '}
                <code>{data.pausedRules.join(', ')}</code>.
              </>
            ) : null}
          </>
        )}
      </div>
      {isLoading || !data ? (
        <div className="px-5 pb-4 text-sm text-gray-two">Loading…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className={tableClass}>
            <thead className={theadClass}>
              <tr>
                <th className={thClass}>Rule</th>
                <th className={thClass}>Template</th>
                <th className={thClass}>Schedule</th>
                <th className={thClass}>Last run</th>
                <th className={`${thClass} text-right`}>Due now</th>
                <th className={thClass}></th>
              </tr>
            </thead>
            <tbody>
              {data.rules.map((r) => (
                <tr key={r.key}>
                  <td className={tdClass}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-four">
                        {r.key}
                      </span>
                      {r.paused && (
                        <span title="Schedule held by AUTOMATION_PAUSED_RULES — Preview and Run now still work">
                          <Pill value="paused" tone="warn" />
                        </span>
                      )}
                    </div>
                    <div className="max-w-md text-xs text-gray-bold">
                      {r.description}
                    </div>
                    {r.note && (
                      <div className="text-xs text-gray-two">{r.note}</div>
                    )}
                  </td>
                  <td className={tdClass}>
                    {r.template ? (
                      <span className="text-xs">{r.template.name}</span>
                    ) : (
                      <span className="text-xs text-red-one">
                        template not found
                      </span>
                    )}
                    <div className="text-xs text-gray-two">
                      {categoryLabel(r.category)}
                    </div>
                  </td>
                  <td className={`${tdClass} font-mono text-xs`}>{r.cron}</td>
                  <td className={`${tdClass} text-xs`}>
                    {r.lastRun ? (
                      <>
                        <div>
                          {fmtRunAt(r.lastRun.at)}
                          {r.lastRun.preview ? ' · preview' : ''}
                          {r.lastRun.trigger === 'cron' ? ' · scheduled' : ''}
                        </div>
                        <div className="text-gray-bold">
                          {countsLine(r.lastRun.counts)}
                        </div>
                        {r.lastRun.error && (
                          <div className="text-red-one">{r.lastRun.error}</div>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-two">never</span>
                    )}
                  </td>
                  <td className={`${tdClass} text-right`}>
                    {r.dueError ? (
                      <span className="text-xs text-red-one" title={r.dueError}>
                        error
                      </span>
                    ) : (
                      <>
                        <span className="font-medium">{r.dueCount ?? '—'}</span>
                        {r.dueSkipped ? (
                          <div className="text-xs text-gray-two">
                            +{r.dueSkipped} skipped
                          </div>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td className={`${tdClass} whitespace-nowrap text-right`}>
                    <button
                      type="button"
                      className={btnGhost}
                      disabled={busyKey === r.key}
                      onClick={() => run.mutate({ key: r.key, preview: true })}
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      className={`${btnSecondary} ml-1`}
                      disabled={busyKey === r.key || mode === 'off'}
                      title={
                        mode === 'off'
                          ? 'EMAIL_AUTOMATIONS is off — preview only'
                          : undefined
                      }
                      onClick={() => run.mutate({ key: r.key, preview: false })}
                    >
                      Run now
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {result && result.ran && (
        <div className="border-t border-white-three px-5 py-3">
          <div className="mb-2 flex items-center justify-between text-xs text-gray-bold">
            <span>
              <b>{result.key}</b> · {result.preview ? 'preview' : 'run'} ·{' '}
              {countsLine(result.counts)} · {result.durationMs} ms
              {result.itemsTruncated
                ? ` · ${result.itemsTruncated} more not shown`
                : ''}
            </span>
            <button
              type="button"
              className={btnGhost}
              onClick={() => setResult(null)}
            >
              Dismiss
            </button>
          </div>
          {result.error && (
            <div className="mb-2 text-xs text-red-one">{result.error}</div>
          )}
          {result.items.length === 0 ? (
            <div className="text-sm text-gray-two">Nothing due.</div>
          ) : (
            <ul className="grid max-h-72 gap-1 overflow-y-auto text-xs">
              {result.items.map((it) => (
                <li
                  key={it.idempotencyKey}
                  className="flex flex-wrap items-center gap-2 rounded border border-white-three px-2 py-1"
                >
                  <Pill value={it.outcome} tone={outcomeTone(it.outcome)} />
                  <span className="font-medium text-gray-four">{it.label}</span>
                  {it.to.length > 0 && (
                    <span className="text-gray-bold">→ {it.to.join(', ')}</span>
                  )}
                  {it.templateName && (
                    <span className="text-gray-two">{it.templateName}</span>
                  )}
                  {it.reason && (
                    <span className="text-gray-two">— {it.reason}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
};

const OpsComms: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState('');
  const [recordId, setRecordId] = useState('');
  const [toAddr, setToAddr] = useState('');
  const [ccAddr, setCcAddr] = useState('');
  const [bccAddr, setBccAddr] = useState('');
  const [category, setCategory] = useState<TOpsMessageCategory>('manual');
  const [internalCopy, setInternalCopy] = useState(false);
  const [spawnCase, setSpawnCase] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [outboxPage, setOutboxPage] = useState(1);
  const [outboxStatus, setOutboxStatus] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data: status } = useQuery({
    queryKey: ['opsCommsStatus'],
    queryFn: getOpsCommsStatus,
  });
  const live = status?.mode === 'live';

  const { data, isLoading } = useQuery({
    queryKey: ['opsTemplates', q, page],
    queryFn: () => getOpsTemplates({ q: q || undefined, page }),
  });

  const { data: preview, isFetching: rendering } = useQuery({
    queryKey: ['opsTemplateRender', selectedId, recordId],
    queryFn: () => renderOpsTemplate(selectedId, recordId || undefined),
    enabled: Boolean(selectedId),
  });
  const extra = preview as (typeof preview & TRenderExtra) | undefined;

  // A fresh preview suggests a recipient off the rendered record
  useEffect(() => {
    setToAddr(extra?.suggestedTo ?? '');
    setEditing(false);
  }, [selectedId, extra?.suggestedTo]);

  const { data: outbox } = useQuery({
    queryKey: ['opsOutbox', outboxPage, outboxStatus],
    queryFn: () =>
      getOpsOutbox({ page: outboxPage, status: outboxStatus || undefined }),
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['opsOutbox'] });
    queryClient.invalidateQueries({ queryKey: ['opsTemplates'] });
    queryClient.invalidateQueries({ queryKey: ['opsTemplateRender'] });
  };

  const toList = splitAddresses(toAddr);

  const queueMut = useMutation({
    mutationFn: () => {
      const cc = splitAddresses(ccAddr);
      const bcc = splitAddresses(bccAddr);
      return queueOpsMessage({
        templateId: selectedId,
        recordId: recordId || undefined,
        to: toList,
        cc: cc.length ? cc : undefined,
        bcc: bcc.length ? bcc : undefined,
        internalCopy,
        spawnCase,
        category,
      });
    },
    onSuccess: (r) => {
      const caseNote = r.caseId ? ' · Case opened' : '';
      if (r.status === 'sent') {
        showToast(`Sent to ${toList.join(', ')}${caseNote}`, 'success');
      } else if (r.status === 'held') {
        showToast(
          `Held in the outbox — ${
            r.holdReason ?? 'awaiting release'
          }${caseNote}`,
          'success',
        );
      } else if (r.status === 'suppressed') {
        showToast(
          `Not sent — this account has asked for no automated mail${caseNote}`,
          'warning',
        );
      } else if (r.status === 'failed') {
        showToast(
          `Send failed — ${r.error ?? 'no detail returned'}${caseNote}`,
          'error',
        );
      } else {
        showToast(`${r.status}${caseNote}`, 'success');
      }
      if (r.quietAccount && r.status !== 'suppressed') {
        showToast(
          'This account has asked for quiet — manual send went ahead',
          'warning',
        );
      }
      queryClient.invalidateQueries({ queryKey: ['opsOutbox'] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const sendMut = useMutation({
    mutationFn: (id: string) => sendOpsMessage(id),
    onSuccess: (r) => {
      if (r.status === 'sent') showToast('Sent', 'success');
      else if (r.status === 'failed')
        showToast(`Send failed — ${r.error ?? 'no detail returned'}`, 'error');
      else
        showToast(
          `Still ${r.status} — ${r.holdReason ?? 'no reason given'}`,
          'warning',
        );
      queryClient.invalidateQueries({ queryKey: ['opsOutbox'] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const cancelMut = useMutation({
    mutationFn: (id: string) => cancelOpsMessage(id),
    onSuccess: () => {
      showToast('Message cancelled', 'success');
      queryClient.invalidateQueries({ queryKey: ['opsOutbox'] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const saveMut = useMutation({
    mutationFn: () =>
      patchOpsTemplate(selectedId, { subject: editSubject, body: editBody }),
    onSuccess: () => {
      showToast('Template edited — portal-local only', 'success');
      setEditing(false);
      invalidateAll();
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const revertMut = useMutation({
    mutationFn: () =>
      patchOpsTemplate(selectedId, { subject: null, body: null }),
    onSuccess: () => {
      showToast('Reverted to the Salesforce version', 'success');
      setEditing(false);
      invalidateAll();
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const startEditing = () => {
    setEditSubject(extra?.rawSubject ?? '');
    setEditBody(extra?.rawBody ?? '');
    setEditing(true);
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const outboxPages = outbox
    ? Math.max(1, Math.ceil(outbox.total / outbox.pageSize))
    : 1;
  const byStatus = outbox?.byStatus ?? {};
  const outboxAll = Object.values(byStatus).reduce((a, b) => a + b, 0);

  const filterBtn = (value: string, label: string, count: number) => (
    <button
      key={value || 'all'}
      type="button"
      className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
        outboxStatus === value
          ? 'bg-blue-100 text-blue-primary'
          : 'text-gray-bold hover:bg-grey-50'
      }`}
      onClick={() => {
        setOutboxStatus(value);
        setOutboxPage(1);
      }}
    >
      {label} · {count.toLocaleString()}
    </button>
  );

  return (
    <>
      <PageHeader
        title="Communications — templates"
        meta={
          live
            ? "Learn It's real Salesforce templates, rendered against real records. Sending is on from the cx@ mailbox; every message is logged in the outbox. Edits are portal-local overlays; Salesforce is never written."
            : "Learn It's real Salesforce templates, rendered against real records. Queued mail is held in the outbox until sending is switched on. Edits are portal-local overlays; Salesforce is never written."
        }
      />
      {status && <StatusStrip status={status} />}
      <Toolbar>
        <input
          className={`${inputClass} w-72`}
          placeholder="Search templates…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Toolbar>

      <PageBody wide>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card title="Templates">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className={tableClass}>
                    <thead className={theadClass}>
                      <tr>
                        <th className={`${thClass} text-right`}>Sends</th>
                        <th className={thClass}>Template</th>
                        <th className={thClass}>Last used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.items.map((t) => {
                        const edited = (t as typeof t & { edited?: boolean })
                          .edited;
                        return (
                          <tr
                            key={t.id}
                            className={`${rowClickClass} ${
                              t.id === selectedId ? 'bg-blue-100/60' : ''
                            }`}
                            onClick={() => setSelectedId(t.id)}
                          >
                            <td className={`${tdClass} text-right`}>
                              {t.timesUsed.toLocaleString()}
                            </td>
                            <td className={tdClass}>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-four">
                                  {t.name}
                                </span>
                                {edited && (
                                  <Pill value="edited in portal" tone="info" />
                                )}
                              </div>
                              <div className="text-xs text-gray-bold">
                                {t.subject}
                              </div>
                            </td>
                            <td
                              className={`${tdClass} whitespace-nowrap text-xs text-gray-bold`}
                            >
                              {t.lastUsed?.slice(0, 10) ?? '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={page}
                  pages={pages}
                  total={data?.total}
                  onPage={setPage}
                />
              </>
            )}
          </Card>

          {selectedId ? (
            rendering || !preview ? (
              <Card title="Preview">
                <div className="flex items-center justify-center py-20">
                  <LoadingSpinner />
                </div>
              </Card>
            ) : (
              <Card
                title={editing ? 'Edit template (portal-local)' : 'Preview'}
                right={
                  editing ? undefined : (
                    <span className="flex gap-2">
                      {extra?.edited && (
                        <button
                          className={btnSecondary}
                          onClick={() => revertMut.mutate()}
                          disabled={revertMut.isPending}
                        >
                          Revert to Salesforce version
                        </button>
                      )}
                      <button className={btnSecondary} onClick={startEditing}>
                        Edit
                      </button>
                    </span>
                  )
                }
              >
                {editing ? (
                  <div className="grid gap-4 p-5">
                    <div>
                      <label className={labelClass}>Subject</label>
                      <input
                        className={`${inputClass} w-full`}
                        value={editSubject}
                        onChange={(e) => setEditSubject(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Body (merge fields keep working)
                      </label>
                      <textarea
                        className={`${inputClass} h-96 w-full font-mono text-xs`}
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className={btnPrimary}
                        onClick={() => saveMut.mutate()}
                        disabled={saveMut.isPending}
                      >
                        {saveMut.isPending ? 'Saving…' : 'Save override'}
                      </button>
                      <button
                        className={btnGhost}
                        onClick={() => setEditing(false)}
                      >
                        Cancel
                      </button>
                      <span className="ml-auto text-xs text-gray-bold">
                        Saved as a portal overlay — Salesforce keeps its copy
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="border-b border-white-three px-5 py-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-four">
                          {preview.name}
                        </span>
                        {extra?.edited && (
                          <Pill value="edited in portal" tone="info" />
                        )}
                        <span className="text-xs text-gray-bold">
                          {preview.resolvedCount}/{preview.mergeFields.length}{' '}
                          merge fields resolved
                          {preview.sampleRecord &&
                            ` · sample: ${preview.sampleRecord.object} “${preview.sampleRecord.label}”`}
                        </span>
                      </div>
                      <input
                        className={`${inputClass} mt-3 w-full`}
                        placeholder="Render against a specific record id (optional — defaults to the newest matching record)"
                        value={recordId}
                        onChange={(e) => setRecordId(e.target.value)}
                      />
                      <div className="mt-3 text-sm">
                        <span className={`${labelClass} !mb-0 inline`}>
                          Subject:{' '}
                        </span>
                        <span className="font-medium text-gray-four">
                          {preview.renderedSubject || '(none)'}
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="md:col-span-2">
                          <label className={labelClass}>To</label>
                          <input
                            className={`${inputClass} w-full`}
                            placeholder="recipient@example.com, another@example.com"
                            value={toAddr}
                            onChange={(e) => setToAddr(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Cc</label>
                          <input
                            className={`${inputClass} w-full`}
                            placeholder="optional, comma-separated"
                            value={ccAddr}
                            onChange={(e) => setCcAddr(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Bcc</label>
                          <input
                            className={`${inputClass} w-full`}
                            placeholder="optional, comma-separated"
                            value={bccAddr}
                            onChange={(e) => setBccAddr(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Category</label>
                          <select
                            className={`${inputClass} w-full`}
                            value={category}
                            onChange={(e) =>
                              setCategory(e.target.value as TOpsMessageCategory)
                            }
                          >
                            {CATEGORIES.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex flex-col justify-end gap-1.5 pb-1 text-sm text-gray-five">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="ops-check h-4 w-4 accent-blue-primary"
                              checked={internalCopy}
                              onChange={(e) =>
                                setInternalCopy(e.target.checked)
                              }
                            />
                            Bcc cx@ (tracked copy)
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="ops-check h-4 w-4 accent-blue-primary"
                              checked={spawnCase}
                              onChange={(e) => setSpawnCase(e.target.checked)}
                            />
                            Open a Case for this thread
                          </label>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <button
                          className={btnPrimary}
                          disabled={toList.length === 0 || queueMut.isPending}
                          onClick={() => queueMut.mutate()}
                        >
                          {queueMut.isPending
                            ? live
                              ? 'Sending…'
                              : 'Queueing…'
                            : live
                            ? 'Send'
                            : 'Queue (held)'}
                        </button>
                        <span className="text-xs text-gray-bold">
                          {live
                            ? 'Transmits now from the cx@ mailbox and lands in the outbox log'
                            : 'Lands in the outbox and is held until sending is switched on'}
                        </span>
                      </div>
                    </div>
                    {preview.isHtml ? (
                      <iframe
                        title="template preview"
                        className="h-[480px] w-full"
                        sandbox=""
                        srcDoc={preview.renderedHtml}
                      />
                    ) : (
                      <pre className="max-h-[480px] overflow-y-auto whitespace-pre-wrap p-5 font-sans text-sm text-gray-five">
                        {renderWithMarkers(preview.renderedHtml)}
                      </pre>
                    )}
                  </>
                )}
              </Card>
            )
          ) : (
            <Card title="Preview">
              <EmptyState
                title="No template selected"
                hint="Pick a template to preview it rendered with real data"
              />
            </Card>
          )}
        </div>

        <div className="mt-4">
          <AutomationsCard />
        </div>

        <div className="mt-4">
          <Card
            title={`Outbox · ${outbox?.total ?? '…'}`}
            right={
              outbox ? (
                <span className="flex flex-wrap items-center gap-1">
                  {filterBtn('', 'All', outboxAll)}
                  {OUTBOX_STATUSES.map((s) =>
                    filterBtn(s, s, byStatus[s] ?? 0),
                  )}
                </span>
              ) : undefined
            }
          >
            {!outbox || outbox.items.length === 0 ? (
              <EmptyState
                title={
                  outboxStatus ? `Nothing ${outboxStatus}` : 'Nothing queued'
                }
                hint={
                  live
                    ? 'Messages sent or queued from a preview land here'
                    : 'Messages queued from a preview land here and are held until sending is switched on'
                }
              />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className={tableClass}>
                    <thead className={theadClass}>
                      <tr>
                        <th className={thClass}>Queued</th>
                        <th className={thClass}>To</th>
                        <th className={thClass}>Subject</th>
                        <th className={thClass}>Category</th>
                        <th className={thClass}>Status</th>
                        <th className={thClass}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {outbox.items.map((m) => {
                        const note =
                          m.status === 'failed'
                            ? m.error ?? m.holdReason
                            : m.holdReason ?? m.error;
                        const cancellable =
                          m.status === 'held' ||
                          m.status === 'failed' ||
                          m.status === 'queued';
                        return (
                          <tr key={m.id}>
                            <td
                              className={`${tdClass} whitespace-nowrap text-xs text-gray-bold`}
                            >
                              {m.queuedAt
                                ? m.queuedAt.slice(0, 16).replace('T', ' ')
                                : '—'}
                            </td>
                            <td className={tdClass}>
                              <div>
                                {m.to.join(', ') || '—'}
                                {m.bccCount > 0 && (
                                  <span className="ml-1 text-xs text-gray-bold">
                                    +{m.bccCount} bcc
                                  </span>
                                )}
                              </div>
                              {m.redirectedTo && (
                                <div className="text-xs text-yellow-900">
                                  redirected to {m.redirectedTo}
                                </div>
                              )}
                              {m.recordId && (
                                <div className="font-mono text-xs text-gray-bold">
                                  {m.recordId}
                                </div>
                              )}
                            </td>
                            <td className={`${tdClass} max-w-xs`}>
                              <div className="truncate">{m.subject ?? '—'}</div>
                              {m.templateName && (
                                <div className="truncate text-xs text-gray-bold">
                                  {m.templateName}
                                </div>
                              )}
                            </td>
                            <td
                              className={`${tdClass} whitespace-nowrap text-xs text-gray-five`}
                            >
                              {categoryLabel(m.category)}
                            </td>
                            <td className={tdClass}>
                              {outboxPill(m.status, m.holdReason)}
                              {note && (
                                <div className="mt-1 max-w-xs text-xs text-gray-bold">
                                  {note}
                                </div>
                              )}
                            </td>
                            <td className={`${tdClass} text-right`}>
                              <span className="inline-flex gap-1.5">
                                {m.status === 'held' && live && (
                                  <button
                                    className={btnSecondary}
                                    disabled={sendMut.isPending}
                                    onClick={() => sendMut.mutate(m.id)}
                                  >
                                    Send now
                                  </button>
                                )}
                                {m.status === 'failed' && (
                                  <button
                                    className={btnSecondary}
                                    disabled={sendMut.isPending}
                                    onClick={() => sendMut.mutate(m.id)}
                                  >
                                    Retry
                                  </button>
                                )}
                                {cancellable && (
                                  <button
                                    className={btnDanger}
                                    disabled={cancelMut.isPending}
                                    onClick={() => cancelMut.mutate(m.id)}
                                  >
                                    Cancel
                                  </button>
                                )}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={outboxPage}
                  pages={outboxPages}
                  total={outbox.total}
                  onPage={setOutboxPage}
                />
              </>
            )}
          </Card>
        </div>
      </PageBody>
    </>
  );
};

export default OpsComms;
